/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React from 'react';
import {makeStyles} from '@material-ui/core/styles';
import {dlsColor} from '../../../theme/theme';
import {
  getBatteryIndicatorColor,
  getHealthIndicatorColor,
  getSeatbeltIndicatorColor,
  HEALTH_INDICATOR_ICON_SIZES,
  HEALTH_INDICATOR_ICONS,
  HEALTH_INDICATORS_BY_VEHICLE_TYPE,
  HealthIndicatorKey,
  IndicatorStatus,
  SEVERITY_AWARE_HEALTH_INDICATORS,
  VehicleFuelType
} from './vehicleTypeIndicatorConfig';

export interface HealthIndicatorValue {
  value: string;
  status?: IndicatorStatus;
}

export type HealthIndicatorValues = Partial<Record<HealthIndicatorKey, HealthIndicatorValue>>;

interface AssetHealthIndicatorsProps {
  vehicleType: VehicleFuelType;
  values: HealthIndicatorValues;
}

interface HealthIndicatorProps {
  indicatorKey: HealthIndicatorKey;
  value: string;
  status?: IndicatorStatus;
}

const useStyles = makeStyles(() => ({
  health : {
    flex       : '1 0 0',
    display    : 'flex',
    flexWrap   : 'wrap',
    alignItems : 'center',
    minWidth   : 0,
    // per the Figma "Health" flex row's computed `gap: 2px 4px` (row-gap,
    // column-gap) - confirmed against two independent Figma exports of this
    // exact section; a "Card Anatomy" doc annotation describes this as
    // "4px vertical"/"2px horizontal" spacing, which is the reverse of the
    // computed values, so the computed CSS is trusted here ...
    columnGap  : '4px',
    rowGap     : '2px'
  },
  indicator : {
    display    : 'flex',
    alignItems : 'center',
    // per Figma: each chip's declared width equals its icon's own width
    // (24px, or 16px for Fuel) plus its label frame's declared width
    // exactly (e.g. Seatbelt: 24 + 42 = 66) - confirming zero gap between
    // icon and label ...
    gap        : 0
  },
  label : {
    fontFamily    : 'Inter',
    fontWeight    : 600,
    fontSize      : '10px',
    lineHeight    : '14px',
    letterSpacing : '0.5px',
    whiteSpace    : 'nowrap',
    // per the "Diagnostic Severity Color Rules": only the icon recolors by
    // status - the value text always stays Grey 2 ...
    color         : dlsColor('grey2')
  }
}));

// only some indicators actually recolor by severity (see
// SEVERITY_AWARE_HEALTH_INDICATORS); every other indicator always renders
// at its default color regardless of what status is passed in ...
const resolveIndicatorStatus = (indicatorKey: HealthIndicatorKey, status?: IndicatorStatus): IndicatorStatus => (
  SEVERITY_AWARE_HEALTH_INDICATORS.includes(indicatorKey) ? (status || 'default') : 'default'
);

const HealthIndicator = ({indicatorKey, value, status}: HealthIndicatorProps) => {
  const resolvedStatus = resolveIndicatorStatus(indicatorKey, status);
  const classes        = useStyles();
  const Icon           = HEALTH_INDICATOR_ICONS[indicatorKey];
  const color          = indicatorKey === 'seatbelt'
    ? getSeatbeltIndicatorColor(resolvedStatus)
    : indicatorKey === 'deviceBattery'
      ? getBatteryIndicatorColor(resolvedStatus, value)
      : getHealthIndicatorColor(resolvedStatus);
  return (
    <div className={classes.indicator}>
      <Icon size={HEALTH_INDICATOR_ICON_SIZES[indicatorKey]} color={color} />
      <span className={classes.label}>
        {value}
      </span>
    </div>
  );
};

/**
 * Returns whether any indicator applicable to `vehicleType` has a value -
 * used by `AssetSummaryCardDiagnostics` to decide whether the Diagnostic
 * Summary Bar should render at all.
 */
export const hasHealthIndicators = (vehicleType: VehicleFuelType, values: HealthIndicatorValues): boolean => (
  HEALTH_INDICATORS_BY_VEHICLE_TYPE[vehicleType].some(indicatorKey => values[indicatorKey])
);

/**
 * AssetHealthIndicators renders the Health indicators applicable to a
 * given vehicle type, in the ION-7392 Vehicle Type Indicator Rules order.
 * Indicators without a value are omitted (non-applicable), never leaving
 * empty space; wrapped rows keep 2px vertical spacing via the container's
 * own row gap.
 *
 * ```typescript
 *
 *   import {AssetHealthIndicators} from
 *     '@ion/dls/src/components/assetcard/current/AssetHealthIndicators';
 *   ...
 *
 *   <AssetHealthIndicators vehicleType="gas" values={{
 *     seatbelt: {value: 'Buckled'},
 *     assetBattery: {value: '100%'}
 *   }} />
 *
 * ```
 *
 */
export const AssetHealthIndicators = ({vehicleType, values}: AssetHealthIndicatorsProps) => {
  const classes        = useStyles();
  const indicatorKeys = HEALTH_INDICATORS_BY_VEHICLE_TYPE[vehicleType]
    .filter(indicatorKey => values[indicatorKey]);
  return (
    <div className={classes.health}>
      {indicatorKeys.map(indicatorKey => (
        <HealthIndicator key={indicatorKey}
          indicatorKey={indicatorKey}
          value={values[indicatorKey].value}
          status={values[indicatorKey].status} />
      ))}
    </div>
  );
};

export default AssetHealthIndicators;
